import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Card, CardContent, Dialog, DialogContent, Loading, Tab, Tabs, TextArea, TextInput, Toggle, } from '@guildeducationinc/recess';
import DeleteOutlineIcon from '@guildeducationinc/recess/icons/DeleteOutlineIcon';
import { AdminTopNav } from '@src/components/Admin/AdminTopNav';
import { useClients } from '@src/hooks/useClients';
import { getIntegrationsNavLinks } from '@src/utilities/topNavHelper';
import _ from 'lodash';
import { DatePicker, EnumPicker, FIELDS, FieldPicker, FilterPicker, IntegrationConfigDirection, IntegrationConfigPicker, MessageType, Messages, MuiMultiSelect, TrackableButton, useLocale, } from 'pe-core-frontend';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { z } from 'zod';
import { GET_EXPORT, SAVE_EXPORT } from '../../graphql/queries/export';
import { ExportEntityType, emptyExport } from '../../types';
import { ExportValueMappings } from './ExportValueMappings';
export const defaultPayrollItemExportFields = [
    'payroll.created_at',
    'payroll.statement_num',
    'payroll_payment.unique_id',
    'payroll_payment.first_name',
    'payroll_payment.last_name',
    'school_name',
    'program_level',
    'cap_year',
    'tax_year',
    'request_num',
    'request_date',
    'approved_date',
    'type',
    'course_code',
    'title',
    'description',
    'course_grade',
    'course_grade_pct',
    'client_fee_type_name',
    'amount',
    'tax_amount',
    'nontax_amount',
    'payroll_payment.level1_org',
    'payroll_payment.level2_org',
    'payroll_payment.level3_org',
    'payroll_payment.classification',
    'payroll_payment.budget_code',
    'payroll_payment.location_code',
    'payroll_payment.emp_group',
    'payroll_payment.country_code',
    'payroll_payment.custom1',
    'payroll_payment.custom2',
    'payroll_payment.custom3',
    'payroll_payment.custom4',
    'payroll_payment.custom5',
];
export const defaultPayrollPaymentExportFields = [
    'payroll.created_at',
    'payroll.statement_num',
    'unique_id',
    'first_name',
    'last_name',
    'tax_year',
    'amount',
    'tax_amount',
    'nontax_amount',
    'level1_org',
    'level2_org',
    'level3_org',
    'classification',
    'budget_code',
    'location_code',
    'emp_group',
    'country_code',
    'custom1',
    'custom2',
    'custom3',
    'custom4',
    'custom5',
];
export const Export = () => {
    const { t } = useLocale();
    const navigate = useNavigate();
    const emailRegex = /^([\w.%+-]+@[\w.-]+\.[A-Za-z]{2,})(\s*[,;]\s*[\w.%+-]+@[\w.-]+\.[A-Za-z]{2,})*$/;
    const exportValidation = z.object({
        name: z
            .string()
            .min(1, { message: t('{field} is required', { values: { field: t('Name') } }) }),
        notifyExternalEmails: z
            .union([
            z.string().regex(emailRegex, {
                message: t('Must be a valid email or comma separated list of emails'),
            }),
            z.literal(''),
        ])
            .nullable()
            .optional(),
    });
    const exportFieldValidation = z.object({
        columnName: z
            .string()
            .min(1, { message: t('{field} is required', { values: { field: t('Export Field') } }) }),
        fieldId: z
            .number()
            .gt(0, { message: t('{field} is required', { values: { field: t('Column Name') } }) }),
    });
    const { id = '', clientId = '' } = useParams();
    const [export_, setExport] = useState({
        ...emptyExport,
        clientId: Number(clientId),
        entityType: ExportEntityType.PAYROLL_ITEM,
    });
    const [messages, setMessages] = useState([]);
    const [mapValuesIndex, setMapValuesIndex] = useState(-1);
    const [errors, setErrors] = useState({});
    const [optionsIndex, setOptionsIndex] = useState(-1);
    const { find } = useClients({ id: Number(clientId), skip: !clientId });
    const client = clientId ? find(Number(clientId)) : undefined;
    const childClients = useClients({
        parentId: Number(clientId),
        initialPageSize: 500, // try to get all child clients
        skip: !clientId || !client?.isParent,
    });
    const childClientOptions = useMemo(() => {
        return childClients.clients.map(client => ({
            value: client.id,
            label: client.name,
        }));
    }, [childClients.clients]);
    const [saveExport] = useMutation(SAVE_EXPORT);
    const { data, loading } = useQuery(GET_EXPORT, {
        variables: { id: Number(id) },
        skip: isNaN(Number(id)),
    });
    const [defaultFieldsQuery] = useLazyQuery(FIELDS);
    useEffect(() => {
        if (!loading && data) {
            setExport({ ...data.getExport });
        }
    }, [data, loading]);
    useEffect(() => {
        setErrors({});
    }, [export_]);
    const validateForm = () => {
        let success = true;
        const errors = {};
        const result = exportValidation.safeParse(export_);
        if (result.success === false) {
            success = false;
            result.error.issues.forEach(issue => {
                errors[issue.path[0]] = issue.message;
            });
        }
        if (export_.exportFields) {
            export_.exportFields.forEach((item, index) => {
                const result = exportFieldValidation.safeParse(item);
                if (result.success === false) {
                    success = false;
                    result.error.issues.forEach(issue => {
                        errors[`exportFields-${index}-${issue.path[0]}`] = issue.message;
                    });
                }
            });
        }
        if (export_.extraData?.processAllClients === false &&
            export_.extraData?.childClientIds?.length === 0) {
            errors.childClientIds = t('Child clients are required');
            success = false;
        }
        if (export_.extraData?.aggregatePaymentRequestItems === true &&
            export_.entityType === ExportEntityType.PAYROLL_ITEM) {
            const restrictedFields = [
                'course_code',
                'fee_type_name',
                'course_grade_pct',
                'course_grade',
                'title',
                'type',
                'description',
            ];
            export_.exportFields?.forEach((item, index) => {
                if (item.field?.code && restrictedFields.includes(item.field.code)) {
                    errors[`exportFields-${index}-fieldId`] = t('Field "{fieldName}" cannot be used when aggregating payment request items', { values: { fieldName: item.field.name } });
                    success = false;
                }
            });
        }
        if (Object.keys(errors).length > 0) {
            setErrors(errors);
            success = false;
        }
        return success;
    };
    const handleSave = async () => {
        try {
            if (validateForm()) {
                const saved = await saveExport({
                    variables: {
                        input: {
                            ..._.omit(export_, [
                                'id',
                                'createdAt',
                                'updatedAt',
                                '__typename',
                                'lastRunAt',
                                'lastRunStatus',
                                'lastRunMessage',
                                'rangeOffsetDays',
                                'extraData',
                            ]),
                            id: export_.id ? export_.id : null,
                            integrationConfigId: export_.integrationConfigId ? export_.integrationConfigId : null,
                            cronSchedules: export_.cronSchedules ?? null,
                            startDate: export_.startDate ?? null,
                            daysApart: export_.daysApart ? export_.daysApart : null,
                            rangeOffsetDays: export_.rangeOffsetDays ? export_.rangeOffsetDays : null,
                            extraData: {
                                processAllClients: export_.extraData?.processAllClients ?? true,
                                childClientIds: export_.extraData?.childClientIds ?? [],
                                aggregatePaymentRequestItems: export_.extraData?.aggregatePaymentRequestItems ?? false,
                            },
                            exportFields: (export_.exportFields ?? []).map(x => {
                                return {
                                    ..._.omit(x, [
                                        'id',
                                        'createdAt',
                                        'updatedAt',
                                        '__typename',
                                        'field',
                                        'defaultValue',
                                    ]),
                                    id: x.id ? x.id : null,
                                    exportId: export_.id ? export_.id : 0,
                                    defaultValue: x.defaultValue ?? null,
                                };
                            }),
                            publicKey: export_.publicKey ?? '',
                            extension: export_.extension ?? '',
                            shortName: export_.shortName ?? '',
                            filenamePattern: export_.filenamePattern ?? '',
                            notifyExternalEmails: export_.notifyExternalEmails ?? '',
                        },
                    },
                });
                if (saved.data?.saveExport) {
                    setExport({ ...export_, id: saved.data.saveExport.id });
                }
                // message
                setMessages([{ type: MessageType.SUCCESS, value: t('Export Saved') }]);
            }
        }
        catch (ex) {
            if (ex.message) {
                setMessages([{ type: MessageType.ERROR, value: ex.message }]);
            }
        }
    };
    const handleAddMapping = () => {
        const count = export_.exportFields?.length ?? 0;
        setExport({
            ...export_,
            exportFields: [
                ...(export_.exportFields ?? []),
                {
                    id: 0,
                    createdAt: uuid(),
                    clientId: export_.clientId,
                    sortOrder: count + 1,
                    columnName: '',
                    fieldId: 0,
                    valueMappings: {},
                    defaultValue: undefined,
                },
            ],
        });
    };
    const handleDeleteMapping = (index) => {
        if (export_.exportFields) {
            const exportFields = [...export_.exportFields];
            exportFields.splice(index, 1);
            setExport({
                ...export_,
                exportFields,
            });
        }
    };
    const handleMappingOnChange = (index, value, propName) => {
        if (export_.exportFields && index > -1 && index < export_.exportFields.length) {
            const exportFields = [...(export_.exportFields ?? [])];
            exportFields[index] = {
                ...exportFields[index],
                [propName]: value,
            };
            setExport({
                ...export_,
                exportFields,
            });
        }
    };
    const handlMappingFieldOnChange = (index, value, init = false) => {
        setExport(prev => {
            if (prev.exportFields && index > -1 && index < prev.exportFields.length) {
                const exportFields = [...prev.exportFields];
                exportFields[index] = {
                    ...exportFields[index],
                    fieldId: value?.id ?? 0,
                    field: value,
                };
                if (!init && (value === undefined || value.selections)) {
                    exportFields[index].valueMappings = {};
                }
                return { ...prev, exportFields };
            }
            return prev;
        });
    };
    const handleSetDefaultMapping = () => {
        defaultFieldsQuery({
            variables: {
                spec: {
                    clientId: export_.clientId,
                    entityType: export_.entityType,
                    codes: export_.entityType === 'payroll_item'
                        ? defaultPayrollItemExportFields
                        : export_.entityType === 'payroll_payment'
                            ? defaultPayrollPaymentExportFields
                            : [],
                },
            },
        }).then(results => {
            if (!results.loading && results.data) {
                const exportFields = results.data?.fields ?? [];
                setExport({
                    ...export_,
                    exportFields: exportFields.map((field, index) => {
                        return {
                            id: 0,
                            createdAt: uuid(),
                            clientId: export_.clientId,
                            fieldId: field.id,
                            field,
                            sortOrder: index + 1,
                            columnName: field.name,
                            valueMappings: {},
                            defaultValue: null,
                        };
                    }),
                });
            }
        });
    };
    const handleClearMapping = () => {
        setExport({
            ...export_,
            exportFields: [],
        });
    };
    const handleSaveOptions = () => {
        setOptionsIndex(-1);
    };
    return (_jsxs("div", { children: [_jsx(AdminTopNav, { title: t('Integrations'), navLinks: getIntegrationsNavLinks(t, Number(clientId), 'exports') }), _jsx("div", { children: _jsx(Card, { children: _jsxs(CardContent, { children: [_jsx("h4", { className: 'text-3xl pb-3', children: t('Export') }), _jsx(Messages, { messages: messages, onClose: () => setMessages([]) }), _jsxs(Loading, { label: 'Export', loaded: !loading, showChildrenWhileLoading: true, overlay: true, children: [_jsxs(Tabs, { children: [_jsxs(Tab, { title: t('General'), children: [_jsxs("div", { className: 'flex flex-col gap-3 mb-3', children: [_jsx(TextInput, { label: t('Name'), value: export_.name, onChange: event => setExport({ ...export_, name: event.target.value }), errors: errors.name }), _jsx(EnumPicker, { placeholderKey: 'filter.select.return_entity_type', enumType: ExportEntityType, labelPrefix: 'metadata.entityType', onChange: value => setExport({
                                                                    ...export_,
                                                                    entityType: value,
                                                                    exportFields: [],
                                                                    filterId: undefined,
                                                                }), value: export_.entityType }), !client?.isParent && (
                                                            //  If not a parent, show the filter picker
                                                            _jsx(FilterPicker, { clientId: export_.clientId, returnType: export_.entityType, value: export_.filterId ? String(export_.filterId) : '', onChange: value => setExport({ ...export_, filterId: value.id }) })), _jsx(IntegrationConfigPicker, { label: t('Integration Config'), clientId: export_.clientId, direction: IntegrationConfigDirection.OUT, value: String(export_.integrationConfigId), onChange: x => setExport({ ...export_, integrationConfigId: Number(x?.id ?? 0) }) }), _jsx(TextInput, { label: t('Schedule To Run At'), helpText: t('Use cron syntax to create a schedule. Example: 0 0 * * * = midnight every day.'), value: export_.cronSchedules, onChange: event => setExport({ ...export_, cronSchedules: event.target.value }), errors: errors.cronSchedules }), _jsx(DatePicker, { label: t('Starts On'), value: export_.startDate || '', onChange: startDate => setExport(prev => ({ ...prev, startDate })), errors: errors.startDate }), _jsx(TextInput, { label: t('Days Apart'), value: export_.daysApart ? String(export_.daysApart) : '', onChange: event => setExport({ ...export_, daysApart: Number(event.target.value) }), errors: errors.daysApart }), _jsx(TextInput, { label: t('Notify External Emails'), helpText: t('Use a comma separated string for multiple email addresses'), value: export_.notifyExternalEmails, onChange: event => setExport({ ...export_, notifyExternalEmails: event.target.value }), errors: errors.notifyExternalEmails }), _jsx(TextInput, { label: t('Range Offset Days'), helpText: t('Offset sets how many days before export day to include. For example, if export day is Wed and Offset is 3, records from Sun and earlier will be included.'), value: export_.rangeOffsetDays ? String(export_.rangeOffsetDays) : '', onChange: event => setExport({ ...export_, rangeOffsetDays: Number(event.target.value) }), errors: errors.rangeOffsetDays }), _jsx(Toggle, { label: t('Is Active?'), active: export_.isActive, onToggle: value => setExport({ ...export_, isActive: value }) }), _jsx(Toggle, { label: t('Send Empty File?'), active: export_.sendEmptyFile, onToggle: value => setExport({ ...export_, sendEmptyFile: value }) }), _jsx(Toggle, { label: 'Notify On No File Sent?', active: export_.notifyNoFileSent, onToggle: value => setExport({ ...export_, notifyNoFileSent: value }) }), export_.entityType === ExportEntityType.PAYROLL_ITEM && (_jsx(Toggle, { label: t('Aggregate Payment Request Items?'), active: export_.extraData?.aggregatePaymentRequestItems ?? false, onToggle: value => setExport({
                                                                    ...export_,
                                                                    extraData: {
                                                                        ...(export_.extraData || {}),
                                                                        aggregatePaymentRequestItems: value,
                                                                    },
                                                                }) })), client?.isParent && (_jsxs(_Fragment, { children: [_jsx(Toggle, { label: t('Process All Child Clients?'), active: export_.extraData?.processAllClients ?? false, onToggle: value => setExport({
                                                                            ...export_,
                                                                            extraData: {
                                                                                ...(export_.extraData || {}),
                                                                                processAllClients: value,
                                                                            },
                                                                        }) }), export_.extraData?.processAllClients !== true && (_jsx(MuiMultiSelect, { label: t('Child Clients'), options: childClientOptions, className: 'ml-4', value: export_.extraData?.childClientIds ?? [], onChange: value => setExport({
                                                                            ...export_,
                                                                            extraData: {
                                                                                ...(export_.extraData || {}),
                                                                                childClientIds: value.selection.map(v => Number(v.value)),
                                                                            },
                                                                        }), errors: errors.childClientIds }))] }))] }), _jsxs("div", { children: [_jsx("h4", { className: 'text-3xl pb-3', children: t('Mappings') }), _jsxs("div", { className: 'flex gap-1 mb-2', children: [(export_.entityType === ExportEntityType.PAYROLL_ITEM ||
                                                                        export_.entityType === ExportEntityType.PAYROLL_PAYMENT) && (_jsx(TrackableButton, { btnType: 'mui', enText: 'Use Default Mappings', tag: 'export_form', variant: 'outlined', onClick: handleSetDefaultMapping, children: t('Use Default Mappings') })), _jsx(TrackableButton, { btnType: 'mui', enText: 'Clear All', tag: 'export_form', variant: 'outlined', onClick: handleClearMapping, children: t('Clear All') })] }), _jsxs("table", { className: 'min-w-full divide-y divide-gray-900 border border-1 border-gray-900 mb-3', children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { className: 'p-3 text-lg text-left', children: t('Export Field') }), _jsx("th", { className: 'p-3 text-lg text-left', children: t('Column Name') }), _jsx("th", { className: 'p-3 text-lg text-left', children: t('Options') }), _jsx("th", { className: 'p-3 text-lg text-left', children: t('Action') })] }) }), _jsxs("tbody", { className: 'divide-y divide-gray-900', children: [(!export_.exportFields || export_.exportFields.length === 0) && (_jsx("tr", { children: _jsx("td", { colSpan: 4, className: 'text-center p-3', children: t('No mappings found') }) })), export_.exportFields &&
                                                                                export_.exportFields.map((row, index) => (_jsxs("tr", { children: [_jsx("td", { className: 'px-2', children: _jsx(FieldPicker, { isSearchable: true, clientId: export_.clientId, entityType: export_.entityType, value: row.fieldId.toString(), getOptionValue: x => x.id.toString(), onChange: x => handlMappingFieldOnChange(index, x), onInit: x => handlMappingFieldOnChange(index, x, true), errors: errors[`exportFields-${index}-fieldId`] }) }), _jsx("td", { className: 'px-2', children: _jsx(TextInput, { label: '', className: 'mb-2', value: row.columnName, onChange: event => handleMappingOnChange(index, event.target.value, 'columnName'), errors: errors[`exportFields-${index}-columnName`] }) }), _jsx("td", { className: 'px-2', children: _jsx(ExportFieldOptions, { readonly: true, exportField: row }) }), _jsx("td", { className: 'px-2', children: _jsxs("div", { className: 'flex gap-1', children: [_jsx("div", { children: _jsx(TrackableButton, { btnType: 'icon', enText: 'Delete', tag: `export_mapping_${row.id}`, label: t('Delete'), appearance: 'minimal', icon: _jsx(DeleteOutlineIcon, { size: 20 }), onClick: () => handleDeleteMapping(index) }) }), _jsx("div", { children: _jsx(TrackableButton, { btnType: 'mui', enText: 'Options', tag: `export_mapping_${row.id}`, variant: 'outlined', onClick: () => setOptionsIndex(index), children: t('Options') }) }), row.field?.selections && (_jsx("div", { children: _jsx(TrackableButton, { btnType: 'mui', enText: 'Map Values', tag: `export_mapping_${row.id}`, variant: 'outlined', onClick: () => setMapValuesIndex(index), children: t('Map Values') }) }))] }) })] }, `${row.id ? row.id : row.createdAt}-${index}`)))] })] }), _jsx("div", { className: 'flex flexJustify--end', children: _jsx(TrackableButton, { btnType: 'mui', enText: 'Add Mapping', tag: 'export_form', variant: 'outlined', onClick: handleAddMapping, children: t('Add Mapping') }) })] })] }), _jsx(Tab, { title: t('Encryption'), children: _jsxs("div", { className: 'flex flex-col gap-3 mb-3', children: [_jsx(TextArea, { fullWidth: true, autosize: false, label: t('Public Key'), value: export_.publicKey, onChange: event => setExport({ ...export_, publicKey: event.currentTarget.value }), errors: errors.publicKey }), _jsx(TextInput, { label: t('Extension'), helpText: t('Example: .pgp or .gpg'), value: export_.extension, onChange: event => setExport({ ...export_, extension: event.target.value }), errors: errors.extension }), _jsx(TextInput, { label: t('Short Name'), value: export_.shortName, onChange: event => setExport({ ...export_, shortName: event.target.value }), errors: errors.shortName }), _jsx(TextInput, { label: t('Filename Pattern'), value: export_.filenamePattern, onChange: event => setExport({ ...export_, filenamePattern: event.target.value }), errors: errors.filenamePattern })] }) })] }), _jsxs("div", { className: 'flex flexJustify--space_between mt-4', children: [_jsx(TrackableButton, { btnType: 'mui', enText: 'Save', tag: 'export_form', variant: 'contained', onClick: handleSave, children: t('Save') }), _jsx(TrackableButton, { btnType: 'mui', enText: 'Cancel', tag: 'export_form', onClick: () => navigate(`/admin/clients/${clientId}/integrations/exports`), variant: 'outlined', children: t('Cancel') })] })] }), _jsx(Dialog, { isOpen: mapValuesIndex >= 0, onClose: () => setMapValuesIndex(-1), size: 'lg', children: _jsx(DialogContent, { className: 'h-screen', children: mapValuesIndex >= 0 && export_.exportFields && (_jsx(ExportValueMappings, { exportField: export_.exportFields[mapValuesIndex], onSave: (value) => {
                                            handleMappingOnChange(mapValuesIndex, value.valueMappings, 'valueMappings');
                                            setMapValuesIndex(-1);
                                        }, onCancel: () => setMapValuesIndex(-1) })) }) }), _jsx(Dialog, { isOpen: optionsIndex >= 0, onClose: () => setOptionsIndex(-1), size: 'lg', children: _jsxs(DialogContent, { className: 'h-screen flex flex-col gap-3', children: [_jsx("div", { className: 'text-3xl', children: t('Options') }), _jsx("div", { children: optionsIndex >= 0 && export_.exportFields && (_jsx(ExportFieldOptions, { exportField: export_.exportFields[optionsIndex], onChange: (value, propName) => {
                                                    handleMappingOnChange(optionsIndex, value, propName);
                                                }, onCancel: () => setOptionsIndex(-1) })) }), _jsxs("div", { className: 'flex gap-1', children: [_jsx(TrackableButton, { btnType: 'mui', enText: 'Save', tag: 'export_options', size: 'small', onClick: handleSaveOptions, children: t('Save') }), _jsx(TrackableButton, { btnType: 'mui', enText: 'Cancel', tag: 'export_options', size: 'small', variant: 'outlined', onClick: () => setOptionsIndex(-1), children: t('Cancel') })] })] }) })] }) }) })] }));
};
const ExportFieldOptions = ({ readonly = false, exportField, onChange, }) => {
    const { t } = useLocale();
    if (!exportField) {
        return _jsx("div", { children: t('Export field not found') });
    }
    return (_jsxs("div", { children: [readonly && (_jsx("div", { className: 'flex flex-col gap-1', children: exportField.defaultValue && (_jsxs("div", { children: [t('Default Value'), " = ", exportField.defaultValue] })) })), !readonly && onChange && (_jsx("div", { className: 'flex flex-col gap-1', children: _jsx(TextInput, { label: t('Default Value'), value: exportField.defaultValue ?? '', onChange: event => onChange(event.target.value, 'defaultValue') }) }))] }));
};
